import { Component, effect, inject, Input, OnInit, signal, ViewChild, ElementRef, AfterViewInit, AfterViewChecked, HostListener } from '@angular/core';
import { MagicSearchService } from '../../service/magic-search/magic-search.service';
import { ActiveFiltersModel, FilterCreatorsDto, FilterErrorLogsDto, FilterItemDto, FiltersModel } from '../../../../common/general-types/filters.dto';
import { MatSliderModule } from '@angular/material/slider';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription, timer } from 'rxjs';

@Component({
  selector: 'magic-filters',
  standalone: true,
  imports: [MatSliderModule, FormsModule, ReactiveFormsModule],
  templateUrl: './magic-filters.component.html',
  styleUrl: './magic-filters.component.css'
})
export class MagicFiltersComponent implements OnInit, AfterViewInit, AfterViewChecked {

  @Input() showBlueQuery: boolean = false;

  @ViewChild('searchTermInput', { static: false }) searchTermInput!: ElementRef<HTMLInputElement>;

  selectedTab = signal<'location' | 'profile' | 'followers' | 'interests' | 'sort'>('profile');

  public magicSearchService = inject(MagicSearchService);


  filtersForm!: FormGroup;
  filtersObject!: FilterCreatorsDto | FilterErrorLogsDto;

  searchQuery: string | undefined;

  ageRangeDefault = {
    disabled: false,
    max: 65,
    min: 16,
    showTicks: false,
    step: 1,
    thumbLabel: false,
    minValue: 16,
    maxValue: 65,
  }
  selectedFilters: { filter: string, value: any }[] = [];

  subFilters$!: Subscription;
  subscriptionList = new Subscription();

  previousCreatorsCount: number = 0;

  @HostListener('document:keydown.enter', ['$event' as any])
  handleEnterKey(event: KeyboardEvent) {
    this.filterSearch();
  }

  @HostListener('document:keydown.escape', ['$event' as any])
  handleEscapeKey(event: KeyboardEvent) {
    this.closeFilters();
  }

  constructor() {
    effect(() => {
      const fancyActiveFilters = this.magicSearchService.fancyActiveFilters;

      const filters = this.magicSearchService.filtersObjectSignal();

      console.log('filters', filters);
      console.log('fancyActiveFilters', fancyActiveFilters);

      if (filters) {
        const searchTerm = filters.searchTerm && filters.searchTerm !== '' ? filters.searchTerm : null;
        this.setFormValues(filters, fancyActiveFilters);

        this.magicSearchService.fancyActiveFilters = this.magicSearchService.activeFiltersSignal();
      }
    }, { allowSignalWrites: true });

    // Watch for tab and modal changes to focus input
    effect(() => {
      const tab = this.selectedTab();
      const isOpen = this.magicSearchService.filtersOpen();
      
      if (tab === 'profile' && isOpen) {
        setTimeout(() => {
          this.focusSearchInput();
        }, 150);
      }
    });

    this.filtersObject = {};
    this.filtersForm = new FormGroup({
      homeTownSearchTerm: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      searchTerm: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      country: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      gender: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      ageRange: new FormControl(
        {
          value: { min: 16, max: 65 },
          disabled: false
        }, []),
      platform: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      tiktokSize: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      instagramSize: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      youtubeSize: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      snapchatSize: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      interests: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
      sortSelected: new FormControl(
        {
          value: null,
          disabled: false
        }, []),
    })
  }

  ngOnInit(): void {
    this.listenToFormChanges();
  }

  ngAfterViewInit(): void {
    // Focus input when component initializes and profile tab is selected
    this.focusSearchInput();
  }

  ngAfterViewChecked(): void {
    // Focus input when view is checked and conditions are met
    this.focusSearchInput();
  }

  private focusSearchInput(): void {
    // Focus when profile tab is selected and modal is open
    if (this.selectedTab() === 'profile' && 
        this.magicSearchService.filtersOpen() && 
        this.searchTermInput?.nativeElement) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        this.searchTermInput.nativeElement?.focus();
      }, 0);
    }
  }



  /**
 * Observe for changes made inside the filters-drawer form to accurately update totalFilters
 */
  listenToFormChanges(): void {
    this.subFilters$ = this.filtersForm.valueChanges.subscribe({
      next: (formValues) => {
        this.updateTotalFilters(formValues);


        this.selectedFilters = [];
        // console.log('filtersForm: ', this.filtersForm);
        // console.log('filtersForm - value: ', this.filtersForm.value);

        if (this.filtersForm && this.filtersForm.value) {
          Object.keys(this.filtersForm.value).forEach((key: any) => {
            if (this.filtersForm.value[key]) {
              if (key == 'ageRange') {
                this.selectedFilters.push({ filter: key, value: `${this.filtersForm.value[key].min} - ${this.filtersForm.value[key].max}` });
              } else if (key == 'gender') {
                this.selectedFilters.push({ filter: key, value: this.filtersForm.value[key].name });
              } else if (key == 'country') {
                this.selectedFilters.push({ filter: key, value: this.filtersForm.value[key].name });
              } else if (key == 'tiktokSize') {
                this.selectedFilters.push({ filter: 'TikTok', value: `TikTok - ${this.filtersForm.value[key].name}` });
              } else if (key == 'instagramSize') {
                this.selectedFilters.push({ filter: 'Instagram', value: `Instagram - ${this.filtersForm.value[key].name}` });
              } else if (key == 'snapchatSize') {
                this.selectedFilters.push({ filter: 'Snapchat', value: `Snapchat - ${this.filtersForm.value[key].name}` });
              } else if (key == 'youtubeSize') {
                this.selectedFilters.push({ filter: 'YouTube', value: `YouTube - ${this.filtersForm.value[key].name}` });
              } else if (key == 'interests') {
                const uniqueInterests = this.filtersForm.value[key].filter((interest: any, index: number, self: any[]) =>
                  index === self.findIndex((i: any) => i.name === interest.name)
                );
                this.selectedFilters.push({ filter: 'Interests', value: `Interests - ${uniqueInterests.map((interest: any) => interest.name).join(', ')}` });
              } else if (key == 'sortSelected') {
                this.selectedFilters.push({ filter: 'Sort', value: this.filtersForm.value[key].name });
              }
              else {
                this.selectedFilters.push({ filter: key, value: this.filtersForm.value[key] });
              }
            }
          });

          // Remove duplicates based on filter and value combination
          this.selectedFilters = this.selectedFilters.filter((item, index, self) =>
            index === self.findIndex((t) => t.filter === item.filter && t.value === item.value)
          );

          // this.selectedFilters = this.selectedFilters.sort((a, b) => a.filter.localeCompare(b.filter));
        }

      },
      error: error => {
        console.error('filters-drawer error: ', error);
      }
    });
    this.subscriptionList.add(this.subFilters$);
  }

  updateTotalFilters(formValues: any): void {
    this.magicSearchService.fancyTotalFilters.set(Object.keys(formValues).reduce((count, key) => {
      if (formValues[key] !== null && formValues[key] !== '') {
        return count + 1;
      }
      return count;
    }, 0));
  }

  get formControl() {
    return this.filtersForm.controls;
  }

  setFormValues(filtersSignal: FilterCreatorsDto | FilterErrorLogsDto, activeFilters: ActiveFiltersModel | undefined) {
    console.log('>> setFormValues() - filtersSignal', filtersSignal);
    console.log('>> setFormValues() - activeFilters', activeFilters);

    if (filtersSignal.country) {
      if (activeFilters && activeFilters.countryOptions) {
        activeFilters.countryOptions?.forEach(country => {
          if (filtersSignal && filtersSignal.country && filtersSignal.country.length === 1 && (filtersSignal.country[0] === country.id)) {
            this.onSelectedSingleOptionsChange(country, 'country');
          }
        });
      }
    } else {
      this.formControl['country'].setValue(null);
    }

    if (filtersSignal.homeTownSearchTerm) {
      this.formControl['homeTownSearchTerm'].setValue(filtersSignal.homeTownSearchTerm);
    } else {
      this.formControl['homeTownSearchTerm'].setValue(null);
    }

    if (filtersSignal.searchTerm) {
      this.formControl['searchTerm'].setValue(filtersSignal.searchTerm);
    } else {
      this.formControl['searchTerm'].setValue(null);
    }

    if (filtersSignal.gender) {
      if (activeFilters && activeFilters.genderOptions) {
        activeFilters.genderOptions?.forEach(gender => {
          if (filtersSignal && filtersSignal.gender && filtersSignal.gender.length === 1 && (filtersSignal.gender[0] === gender.id)) {
            this.onSelectedSingleOptionsChange(gender, 'gender');
          }
        });
      }
    } else {
      this.formControl['gender'].setValue(null);
    }

    if (filtersSignal.ageFrom) {
      this.formControl['ageRange'].setValue({ min: filtersSignal.ageFrom, max: filtersSignal.ageTo });
      this.ageRangeDefault.minValue = filtersSignal.ageFrom;
    } else {
      this.formControl['ageRange'].setValue({ min: this.ageRangeDefault.min, max: this.ageRangeDefault.max });
    }
    if (filtersSignal.ageTo) {
      this.formControl['ageRange'].setValue({ min: this.formControl['ageRange'].value.min, max: filtersSignal.ageTo });
      this.ageRangeDefault.maxValue = filtersSignal.ageTo;
    }

    // TikTok Followers
    if (filtersSignal.tikTokFollowersFrom || filtersSignal.tikTokFollowersTo) {
      if (activeFilters?.tiktokSizeOptions) {
        const selectedTiktokSize = activeFilters.tiktokSizeOptions.find(option =>
          filtersSignal.tikTokFollowersFrom === option.value &&
          filtersSignal.tikTokFollowersTo === option.value
        );
        if (selectedTiktokSize) this.onSelectedSingleOptionsChange(selectedTiktokSize, 'tiktokSize');
      }
    } else {
      this.formControl['tiktokSize'].setValue(null);
      const currentPlatforms: FilterItemDto[] = this.formControl['platform'].value || [];
      const updatedPlatforms = currentPlatforms.filter(platform => platform.id !== 1);
      if (this.formControl['platform'].value && this.formControl['platform'].value.length && this.formControl['platform'].value.length >= 1) {
        this.formControl['platform'].setValue(updatedPlatforms);
      } else {
        this.formControl['platform'].setValue(null);
      }
    }

    // Instagram Followers
    if (filtersSignal.instagramFollowersFrom || filtersSignal.instagramFollowersTo) {
      if (activeFilters?.instagramSizeOptions) {
        const selectedInstagramSize = activeFilters.instagramSizeOptions.find(option =>
          filtersSignal.instagramFollowersFrom === option.value &&
          filtersSignal.instagramFollowersTo === option.value
        );
        if (selectedInstagramSize) this.onSelectedSingleOptionsChange(selectedInstagramSize, 'instagramSize');
      }
    } else {
      this.formControl['instagramSize'].setValue(null);
      const currentPlatforms: FilterItemDto[] = this.formControl['platform'].value || [];
      const updatedPlatforms = currentPlatforms.filter(platform => platform.id !== 2);
      if (this.formControl['platform'].value && this.formControl['platform'].value.length && this.formControl['platform'].value.length >= 1) {
        this.formControl['platform'].setValue(updatedPlatforms);
      } else {
        this.formControl['platform'].setValue(null);
      }
    }

    // Snapchat Followers
    if (filtersSignal.snapchatFollowersFrom || filtersSignal.snapchatFollowersTo) {
      if (activeFilters?.snapchatSizeOptions) {
        const selectedSnapchatSize = activeFilters.snapchatSizeOptions.find(option =>
          filtersSignal.snapchatFollowersFrom === option.value &&
          filtersSignal.snapchatFollowersTo === option.value
        );
        if (selectedSnapchatSize) this.onSelectedSingleOptionsChange(selectedSnapchatSize, 'snapchatSize');
      }
    } else {
      this.formControl['snapchatSize'].setValue(null);
      const currentPlatforms: FilterItemDto[] = this.formControl['platform'].value || [];
      const updatedPlatforms = currentPlatforms.filter(platform => platform.id !== 4);
      if (this.formControl['platform'].value && this.formControl['platform'].value.length && this.formControl['platform'].value.length >= 1) {
        this.formControl['platform'].setValue(updatedPlatforms);
      } else {
        this.formControl['platform'].setValue(null);
      }
    }

    // YouTube Followers
    if (filtersSignal.youtubeSubscribersFrom || filtersSignal.youtubeSubscribersTo) {
      if (activeFilters?.youtubeSizeOptions) {
        const selectedYouTubeSize = activeFilters.youtubeSizeOptions.find(option =>
          filtersSignal.youtubeSubscribersFrom === option.value &&
          filtersSignal.youtubeSubscribersTo === option.value
        );
        if (selectedYouTubeSize) this.onSelectedSingleOptionsChange(selectedYouTubeSize, 'youtubeSize');
      }
    } else {
      this.formControl['youtubeSize'].setValue(null);
      const currentPlatforms: FilterItemDto[] = this.formControl['platform'].value || [];
      const updatedPlatforms = currentPlatforms.filter(platform => platform.id !== 3);
      if (this.formControl['platform'].value && this.formControl['platform'].value.length && this.formControl['platform'].value.length >= 1) {
        this.formControl['platform'].setValue(updatedPlatforms);
      } else {
        this.formControl['platform'].setValue(null);
      }
    }

    if (filtersSignal.interests && filtersSignal.interests.length) {
      if (activeFilters?.interestsOptions) {
        const selectedInterests = activeFilters.interestsOptions.filter(interest =>
          filtersSignal.interests?.includes(interest.id as number)
        );
        this.onSelectedMultipleOptionsChange(selectedInterests, 'interests');
      }
    } else {
      this.formControl['interests'].setValue(null);
    }
  }


  onAgeRangeChange(event: Event, type: 'min' | 'max') {
    if (type === 'min') {
      this.formControl['ageRange'].setValue({ min: event, max: this.formControl['ageRange'].value.max });
    } else {
      this.formControl['ageRange'].setValue({ min: this.formControl['ageRange'].value.min, max: event });
    }
  }

  onSelectedSingleOptionsChange(selectedOption: FilterItemDto, formControl: string): void {
    console.log('>> onSelectedSingleOptionsChange() - selectedOption', selectedOption);
    console.log('>> onSelectedSingleOptionsChange() - formControl', formControl);
    this.formControl[formControl].setValue(selectedOption);
    if (!selectedOption) {
      this.formControl[formControl].setValue(null);
    }
  }

  onSelectedMultipleOptionsChange(selectedOptions: FilterItemDto[], formControl: string): void {
    this.magicSearchService.fancyInterestsSelected = selectedOptions.length;
    this.formControl[formControl].setValue(selectedOptions);
    if (!selectedOptions.length) {
      this.formControl[formControl].setValue(null);
    }
  }


  uiOneOfMultipleSelectionChange(selectedOption: FilterItemDto, formControl: string): void {
    console.log('>> uiOneOfMultipleSelectionChange() - selectedOption', selectedOption);
    console.log('>> uiOneOfMultipleSelectionChange() - formControl', formControl);
    this.magicSearchService.fancyInterestsSelected += 1 || 1;
    if (this.formControl[formControl].value && this.formControl[formControl].value.length && this.formControl[formControl].value.length >= 1) {
      const currentInterests = this.formControl[formControl].value;
      const updatedInterests = [...currentInterests, selectedOption];
      this.formControl[formControl].setValue(updatedInterests);
    } else {
      this.formControl[formControl].setValue([selectedOption]);
    }
    // console.log('>> uiOneOfMultipleSelectionChange() - formControl', this.formControl[formControl].value);
  }

  filterSearch() {
    console.log('>> filterSearch()');

    const constructedFilters = {
      ageOptions: this.formControl['ageRange'].value,
      interests: this.formControl['interests'].value,
      country: this.formControl['country'].value,
      gender: this.formControl['gender'].value,
      platform: this.formControl['platform'].value,
      tiktokSize: this.formControl['tiktokSize'].value,
      instagramSize: this.formControl['instagramSize'].value,
      youtubeSize: this.formControl['youtubeSize'].value,
      snapchatSize: this.formControl['snapchatSize'].value,
      // isVerified: this.formControl['isVerified'].value,
      sort: this.formControl['sortSelected'].value,
      // brand: this.formControl['brand'].value,
      // briefs: this.formControl['briefs'].value,
      homeTownSearchTerm: this.formControl['homeTownSearchTerm'].value,
      searchTerm: this.formControl['searchTerm'].value,
      // date_range: this.formControl['date_range'].value,
      // recruitGroup: this.formControl['recruitGroup'].value
    }

    console.log('constructedFilters', constructedFilters);

    this.magicSearchService.appliedFilters = this.setFilterValues(constructedFilters);

    this.magicSearchService.filtersObjectSignal.set(constructedFilters);
    // this.magicSearchService.filterErrorLogsObjectSignal.set(this.filtersObject);

    this.magicSearchService.kickOffBlueSearch(this.magicSearchService.appliedFilters)
    this.closeFilters();
  }

  setFilterValues(filters: FiltersModel) {
    let constructedFilters: FilterCreatorsDto = {};

    if (filters.homeTownSearchTerm) {
      constructedFilters.homeTownSearchTerm = filters.homeTownSearchTerm.toLowerCase();
    }

    if (filters.searchTerm) {
      constructedFilters.searchTerm = filters.searchTerm.toLowerCase();
    }

    if (filters.country) {
      constructedFilters.country = [filters.country.id as string];
    } else {
      if (constructedFilters.country) {
        delete constructedFilters.country;
      }
    }
    if (filters.gender) {
      constructedFilters.gender = [filters.gender.id as number];
    } else {
      if (constructedFilters.gender) {
        delete constructedFilters.gender;
      }
    }

    if (filters.ageOptions) {
      constructedFilters.ageFrom = filters.ageOptions.min;
      constructedFilters.ageTo = filters.ageOptions.max;
    } else {
      if (constructedFilters.ageFrom) {
        delete constructedFilters.ageFrom;
      }
      if (constructedFilters.ageTo) {
        delete constructedFilters.ageTo;
      }
    }

    if (filters.tiktokSize) {
      constructedFilters.tikTokFollowersFrom = filters.tiktokSize.value;
      constructedFilters.tikTokFollowersTo = filters.tiktokSize.value;
    } else {
      if (constructedFilters.tikTokFollowersFrom) {
        delete constructedFilters.tikTokFollowersFrom;
      }
      if (constructedFilters.tikTokFollowersTo) {
        delete constructedFilters.tikTokFollowersTo;
      }
    }

    if (filters.instagramSize) {
      constructedFilters.instagramFollowersFrom = filters.instagramSize.value;
      constructedFilters.instagramFollowersTo = filters.instagramSize.value;
    } else {
      if (constructedFilters.instagramFollowersFrom) {
        delete constructedFilters.instagramFollowersFrom;
      }
      if (constructedFilters.instagramFollowersTo) {
        delete constructedFilters.instagramFollowersTo;
      }
    }

    if (filters.snapchatSize) {
      constructedFilters.snapchatFollowersFrom = filters.snapchatSize.value;
      constructedFilters.snapchatFollowersTo = filters.snapchatSize.value;
    } else {
      if (constructedFilters.snapchatFollowersFrom) {
        delete constructedFilters.snapchatFollowersFrom;
      }
      if (constructedFilters.snapchatFollowersTo) {
        delete constructedFilters.snapchatFollowersTo;
      }
    }

    if (filters.youtubeSize) {
      constructedFilters.youtubeSubscribersFrom = filters.youtubeSize.value;
      constructedFilters.youtubeSubscribersTo = filters.youtubeSize?.value;
    } else {
      if (constructedFilters.youtubeSubscribersFrom) {
        delete constructedFilters.youtubeSubscribersFrom;
      }
      if (constructedFilters.youtubeSubscribersTo) {
        delete constructedFilters.youtubeSubscribersTo;
      }
    }

    if (filters.interests) {
      constructedFilters.interests = filters.interests.map((interest: any) => interest.id as number);
    } else {
      if (constructedFilters.interests) {
        delete constructedFilters.interests;
      }
    }

    if (filters.sort) {
      constructedFilters.sortField = filters.sort.value as string;
      constructedFilters.sortOrder = filters.sort.direction as string;
    }
    else {
      constructedFilters.sortField = 'creator.average_rating';
      constructedFilters.sortOrder = 'DESC';
    }

    return constructedFilters;
  }

  resetFilters() {
    console.log('>> resetFilters()');
    this.formControl['ageRange'].setValue({ min: 16, max: 65 });
    this.formControl['interests'].setValue(null);
    this.formControl['country'].setValue(null);
    this.formControl['gender'].setValue(null);
    this.formControl['platform'].setValue(null);
    this.formControl['tiktokSize'].setValue(null);
    this.formControl['instagramSize'].setValue(null);
    this.formControl['youtubeSize'].setValue(null);
    this.formControl['snapchatSize'].setValue(null);
    this.formControl['sortSelected'].setValue(null);
    this.formControl['homeTownSearchTerm'].setValue(null);
    this.magicSearchService.pageNumber.set(0);
    this.magicSearchService.blueSearchResults.set(undefined);
    this.magicSearchService.purpleSearchResults.set(undefined);
    this.magicSearchService.pinkSearchResults.set(undefined);
  }

  setFiltersTab(tab: 'location' | 'profile' | 'followers' | 'interests' | 'sort') {
    this.selectedTab.set(tab);
    // Focus input when profile tab is selected
    if (tab === 'profile') {
      setTimeout(() => {
        this.focusSearchInput();
      }, 100);
    }
  }

  getTabClass(tab: 'location' | 'profile' | 'followers' | 'interests' | 'sort') {
    return this.selectedTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }


  openFilters() {
    this.magicSearchService.filtersOpen.set(true);
    // Focus input after modal opens
    setTimeout(() => {
      this.focusSearchInput();
    }, 100);
  }

  closeFilters() {
    this.magicSearchService.filtersOpen.set(false);
  }
}
